import type { LensCard, LensCardView, LensDataset, LensDatasetField, LensFilter } from '~/types/leLens';

import {
  type Consolidation,
  acceptsDashboardPeriod,
  bestDatasetSql,
  datasetFaults,
  dateColumnFromBinding,
  extractConsolidation,
  extractDateDeclaration,
  filterableColumns,
  preferredDataset,
  resolvedProjection,
  returnsDeclaredColumns,
} from './buildPlan';
import type { Ask } from './buildSession';
import { viewResolvesAgainst } from './cardDatasetBinding';
import type { Check, SqlVerdict } from './checkSql';
import { HEADLINE_MEASURE, IDENTIFIER_NAME, PRE_AVERAGED_NAME, RATIO_NAME, UNIT_SUFFIX } from './columnClassification';
import { prepareFields } from './datasetFields';
import type { BuildPool } from './lensAskSession';
import { type VerifiedSource, consolidatePrompt, datasetPrompt } from './prompts';

export type CombineOutcome = {
  datasets: Array<LensDataset>;
  cards: Array<LensCard>;
  /** One-reader sources replaced only after their sole card has moved to a
   * reviewed shared query. Sources with any card still reading them remain. */
  replacedDatasetIds?: Array<string>;
  /** Said to the reader whatever the result, including when nothing changed. */
  note: string;
  /** What happens to each card that owns a query, so the review names every one. */
  decisions: Array<CombineDecision>;
  /** Distinct queries the dashboard runs now, and would run after applying. */
  cost: { queriesBefore: number; queriesAfter: number };
};

export type CombineDecision =
  | { cardId: string; title: string; kind: 'moves'; datasetId: string; datasetName: string }
  | { cardId: string; title: string; kind: 'keeps'; reason: KeptReason };

/** Why a card was left on its own query. One reason, one sentence, no jargon. */
export type KeptReason =
  | 'no-shared-grain'
  | 'no-cut'
  | 'columns-missing'
  | 'would-change-the-number'
  | 'source-not-equivalent'
  | 'not-written';

const KEPT_REASONS: Record<KeptReason, string> = {
  'no-shared-grain': 'These ask for data at a different level of detail, so no shared query fits them.',
  'no-cut': 'The shared query did not come back with a way for these to read it.',
  'columns-missing': 'The shared query does not return everything these cards show.',
  'would-change-the-number': 'Moving these would change what they measure, so they were left alone.',
  'source-not-equivalent': 'We could not verify that these read the same source data, so they were left alone.',
  'not-written': 'The shared query for these could not be written.',
};

export function describeKeptReason(reason: KeptReason): string {
  return KEPT_REASONS[reason];
}

/**
 * Distinct queries a set of cards makes the dashboard run.
 *
 * Cards reading one shared dataset are one query on the server. This is the
 * number beside the dashboard title, and the same rule has to answer "how many
 * now, how many after" or the review would promise a saving the title does not
 * show.
 */
export function distinctQueryCount(cards: ReadonlyArray<LensCard>): number {
  return new Set(cards.flatMap((card) => (card.datasetId ? [card.datasetId] : []))).size;
}

/**
 * What this dashboard's queries cost right now, said without the word dataset.
 *
 * Stated whether or not anything can be combined: a reader who opens this to
 * check the cost of their board should not have to run a search to find out
 * there is nothing to find.
 */
export function describeQueryPosture(cards: ReadonlyArray<LensCard>): string {
  const total = distinctQueryCount(cards);
  const datasetReaders = new Map<string, number>();
  cards.forEach((card) => {
    if (card.datasetId) {
      datasetReaders.set(card.datasetId, (datasetReaders.get(card.datasetId) ?? 0) + 1);
    }
  });
  const oneReaderDatasetCount = [...datasetReaders.values()].filter((readers) => readers === 1).length;
  if (!total) {
    return 'This Lens has no linked query sources yet.';
  }
  const sources = `${total} query ${total === 1 ? 'source' : 'sources'}`;
  return oneReaderDatasetCount > 1
    ? `${sources}. Check for shared queries to reduce duplication.`
    : `${sources} across ${cards.length} ${cards.length === 1 ? 'card' : 'cards'}.`;
}

/** A BigQuery dry run supplies source evidence the model cannot provide. */
export type CombineSourceCheck = {
  columns: LensDataset['columns'];
  bytesProcessed: number;
  referencedTables: Array<string>;
};

type CombineCandidate = {
  card: LensCard;
  sourceSql: string;
  sourceDatasetId?: string;
};

/**
 * Puts the cards of an existing board onto shared queries, where they honestly
 * fit.
 *
 * A board built card by card carries one query per card: every one is a
 * separate scan of the warehouse for a number that often sits beside the one
 * next to it. This asks for the same data model the build path asks for, and
 * repoints the cards it can onto it.
 *
 * "Where they honestly fit" is the whole constraint. A card whose grain no
 * proposed dataset carries keeps its own query. Forcing it onto one would make
 * its number quietly wrong, which is worse than another query.
 */
export async function combineQueries({
  cards,
  datasets = [],
  filters,
  existingIds,
  pool,
  onStatus,
  verifySource,
}: {
  cards: Array<LensCard>;
  datasets?: Array<LensDataset>;
  filters: Array<LensFilter>;
  existingIds: Array<string>;
  pool: BuildPool;
  onStatus: (message: string) => void;
  /** Checks a query without running it and returns BigQuery's source tables.
   * A candidate cannot move without this evidence. */
  verifySource: (sql: string) => Promise<CombineSourceCheck>;
}): Promise<CombineOutcome> {
  const ask: Ask = (question) => pool.primary.ask(question, { onProgress: (progress) => onStatus(progress ?? '') });
  const readersByDatasetId = new Map<string, number>();
  cards.forEach((card) => {
    if (card.datasetId) readersByDatasetId.set(card.datasetId, (readersByDatasetId.get(card.datasetId) ?? 0) + 1);
  });
  const datasetById = new Map(datasets.map((dataset) => [dataset.id, dataset]));
  // Never disturb a source another card still reads: preserving that source
  // protects every unaffected card by default.
  const candidates: Array<CombineCandidate> = cards.flatMap((card) => {
    const source = card.datasetId ? datasetById.get(card.datasetId) : undefined;
    return source && readersByDatasetId.get(source.id) === 1
      ? [{ card, sourceDatasetId: source.id, sourceSql: source.sql }]
      : [];
  });
  const queriesBefore = distinctQueryCount(cards);
  const unchanged = (note: string, reason?: KeptReason): CombineOutcome => ({
    cards,
    datasets: [],
    note,
    decisions: reason
      ? candidates.map(({ card }) => ({ cardId: card.id, title: card.title, kind: 'keeps', reason }))
      : [],
    cost: { queriesBefore, queriesAfter: queriesBefore },
  });

  if (candidates.length < 2) {
    return unchanged('There is only one query here, so there is nothing to share.');
  }

  onStatus('Looking for queries that can be shared…');
  const consolidated = await ask(
    consolidatePrompt(
      candidates.map(({ card, sourceSql }) => ({
        // The consolidator must see the actual source, period boundary and
        // filters it is being asked to preserve. A title alone made it able
        // to invent a plausible but differently scoped replacement.
        request: `${card.description ?? card.title}\n\nExisting query to preserve exactly in source, filters, measures and period:\n\`\`\`sql\n${sourceSql}\n\`\`\``,
        title: card.title,
        type: card.type,
      })),
    ),
  );
  const model = extractConsolidation(consolidated.answer, candidates.length);

  if (!model?.datasets.length) {
    return unchanged(
      'No two of these cards ask for data at the same level of detail, so each keeps its own query.',
      'no-shared-grain',
    );
  }

  const { datasets: generatedDatasets, idByKey } = await writeDatasets({ pool, existingIds, filters, model, onStatus });

  if (!generatedDatasets.length) {
    return unchanged('A shared query was proposed but none could be written, so nothing changed.', 'not-written');
  }

  // Neither a model's declaration nor similarly named columns establish that
  // two queries even draw from the same sources. Dry-run both sides and use
  // BigQuery's source-table facts as a minimum guard before offering a card
  // move. The reviewer still sees and accepts the proposed query; a failed or
  // empty check is deliberately a refusal.
  onStatus('Checking the source tables behind the proposed shared query…');
  const checks = new Map<string, CombineSourceCheck | undefined>();
  await Promise.all(
    [
      ...candidates.map((candidate) => [candidate.card.id, candidate.sourceSql] as const),
      ...generatedDatasets.map((dataset) => [dataset.id, dataset.sql] as const),
    ].map(async ([id, sql]) => {
      try {
        const check = await verifySource(sql);
        checks.set(id, sourceFingerprint(check.referencedTables) ? check : undefined);
      } catch {
        checks.set(id, undefined);
      }
    }),
  );

  const columnsById = new Map(
    generatedDatasets.map((dataset) => {
      const planned = model.datasets.find((entry) => idByKey.get(entry.key) === dataset.id);
      // Checked one promised column at a time against the written query,
      // rather than parsed into a list of its own: returnsDeclaredColumns
      // (buildPlan.ts) already forwards every column when the query returns a
      // star, since a star over a table we cannot see into might hold any of
      // them, and there is no evidence either way to refuse on.
      const returned = (planned?.columns ?? []).filter((column) => returnsDeclaredColumns(dataset.sql, [column]));
      return [dataset.id, [...returned, ...(dataset.fields ?? []).map((f) => f.name)]] as const;
    }),
  );
  const nameById = new Map(generatedDatasets.map((dataset) => [dataset.id, dataset.name]));

  const assignmentByCardId = new Map(
    // The model numbers cards from one, which is how the prompt asks for them.
    model.assignments.flatMap((assignment) => {
      const candidate = candidates[assignment.index - 1];
      return candidate ? [[candidate.card.id, assignment] as const] : [];
    }),
  );

  const decisions: Array<CombineDecision> = [];
  const bindingByCardId = new Map<string, { datasetId: string; view: LensCardView }>();

  for (const candidate of candidates) {
    const { card } = candidate;
    const assignment = assignmentByCardId.get(card.id);
    const datasetId = assignment && idByKey.get(assignment.datasetKey);
    const keep = (reason: KeptReason) => decisions.push({ cardId: card.id, title: card.title, kind: 'keeps', reason });

    if (!assignment) {
      keep('no-shared-grain');
      continue;
    }
    if (!datasetId) {
      keep('not-written');
      continue;
    }
    const currentSource = checks.get(card.id);
    const proposedSource = checks.get(datasetId);
    if (
      !currentSource ||
      !proposedSource ||
      !sameSourceData(currentSource.referencedTables, proposedSource.referencedTables)
    ) {
      keep('source-not-equivalent');
      continue;
    }
    if (!assignment.view) {
      // Repointing a card with no cut would leave it reading its old cut
      // against a different result set, which draws either nothing or the
      // wrong thing.
      keep('no-cut');
      continue;
    }
    // Checked against the query as written rather than as promised: a dataset
    // is kept when it returns some of the columns it declared, so a cut naming
    // one of the others would draw an empty card that reads as "no data".
    if (!viewResolvesAgainst(assignment.view, columnsById.get(datasetId) ?? [])) {
      keep('columns-missing');
      continue;
    }
    if (wouldChangeTheNumber(card, assignment.view, candidate.sourceSql)) {
      keep('would-change-the-number');
      continue;
    }
    bindingByCardId.set(card.id, { datasetId, view: assignment.view });
    decisions.push({
      cardId: card.id,
      title: card.title,
      kind: 'moves',
      datasetId,
      datasetName: nameById.get(datasetId) ?? datasetId,
    });
  }

  const bound = new Set([...bindingByCardId.values()].map((binding) => binding.datasetId));
  const replacedDatasetIds = candidates.flatMap((candidate) =>
    candidate.sourceDatasetId && bindingByCardId.has(candidate.card.id) ? [candidate.sourceDatasetId] : [],
  );
  const moved = cards.map((card) => {
    const assigned = bindingByCardId.get(card.id);
    if (!assigned) {
      return card;
    }
    return { ...card, datasetId: assigned.datasetId, view: assigned.view };
  });

  // A dataset every one of its cards was refused would be a query run for no
  // one, and a saving the reader was promised but does not get.
  const shared = generatedDatasets
    .filter((dataset) => bound.has(dataset.id))
    .map((dataset) => {
      const check = checks.get(dataset.id);
      return check
        ? {
            ...dataset,
            columns: check.columns,
            dryRun: {
              at: new Date().toISOString(),
              bytesProcessed: check.bytesProcessed,
              referencedTables: check.referencedTables,
            },
          }
        : dataset;
    });
  const movedCount = bindingByCardId.size;
  const keptCount = candidates.length - movedCount;
  const queriesAfter = movedCount ? distinctQueryCount(moved) : queriesBefore;

  if (!movedCount) {
    return {
      cards,
      datasets: [],
      note: 'Nothing could be shared safely, so every card keeps its own query.',
      decisions,
      cost: { queriesBefore, queriesAfter: queriesBefore },
    };
  }

  return {
    cards: moved,
    datasets: shared,
    note: keptCount
      ? `${movedCount} of ${candidates.length} cards can share ${shared.length} ${shared.length === 1 ? 'query' : 'queries'}. ${keptCount} ${keptCount === 1 ? 'keeps its own' : 'keep their own'}.`
      : `All ${candidates.length} cards can share ${shared.length} ${shared.length === 1 ? 'query' : 'queries'}.`,
    decisions,
    cost: { queriesBefore, queriesAfter },
    ...(replacedDatasetIds.length ? { replacedDatasetIds } : {}),
  };
}

/** A source is usable as evidence only when BigQuery reported at least one
 * table. The sorted fingerprint makes equivalent join order stable. */
function sourceFingerprint(tables: ReadonlyArray<string>): string | undefined {
  const unique = [...new Set(tables.map((table) => table.trim().toLowerCase()).filter(Boolean))].sort();
  return unique.length ? unique.join('\n') : undefined;
}

function sameSourceData(left: ReadonlyArray<string>, right: ReadonlyArray<string>): boolean {
  const fingerprint = sourceFingerprint(left);
  return Boolean(fingerprint) && fingerprint === sourceFingerprint(right);
}

/**
 * Whether moving a card onto a shared query would change what it measures.
 *
 * Sharing a query is meant to change where the numbers come from, not what
 * they are. A card that already says how to reduce its rows is carrying an
 * instruction somebody chose, so a proposal that quietly sums what was being
 * averaged, or shows a total where the card asked for a rate, is refused.
 *
 * A card built card by card carries no such instruction: its meaning lives in
 * its own query. Every card on a real sixteen-card board arrived that way, and
 * a guard that only read declared cuts let a bookings count, a margin total
 * and two percentages all be repointed at a sum of TTV. So with no cut to
 * compare, the card's own projection says what it measures, and a proposal
 * that measures a different kind of thing is refused. No evidence either way
 * lets the proposal stand, which is the same stance as before.
 */
function wouldChangeTheNumber(card: LensCard, proposed: LensCardView, sourceSql?: string): boolean {
  const current = card.view;
  if (current) {
    if (current.aggregate && proposed.aggregate && current.aggregate !== proposed.aggregate) {
      return true;
    }
    return Boolean(current.ratio) !== Boolean(proposed.ratio);
  }
  const own = measureFamilies(projectedAliases(sourceSql ?? ''));
  if (!own.size) {
    return false;
  }
  const next = proposed.ratio ? 'ratio' : measureFamily(proposed.value ?? '');
  // A rate only ever moves onto a rate, and a total never replaces one.
  if (own.has('ratio') !== (next === 'ratio')) {
    return true;
  }
  if (next === 'ratio' || next === null) {
    return false;
  }
  if (next === 'count') {
    return !own.has('count');
  }
  return !(own.has(next) || own.has('money'));
}

/**
 * The projected column names or aliases in a flat SELECT list, good enough to
 * guess a measure's family from its own name.
 *
 * Not the dataset-column resolution that used to live in
 * workspaceCandidates.ts (removed: every dataset's columns are declared now,
 * from `dataset.columns`). A card that still carries its own query and no
 * `view` has no dataset to declare anything on: that is exactly the shape
 * A3's migration turns into a one-card dataset, and until a card is
 * normalised this way its own projection is the only signal
 * `wouldChangeTheNumber` has for what it measures. It only ever gates a
 * decision to keep a card on its own query, never to bind one onto a
 * dataset's declared columns, so a rough guess here costs nothing worse than
 * an extra query the card already had.
 */
function splitTopLevel(text: string): Array<string> {
  const parts: Array<string> = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth = Math.max(0, depth - 1);
    } else if (char === ',' && depth === 0) {
      parts.push(text.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(text.slice(start));
  return parts.map((part) => part.trim()).filter(Boolean);
}

const AS_ALIAS = /\bas\s+`?([A-Za-z_][A-Za-z0-9_]*)`?\s*$/i;
const BARE_IDENTIFIER = /(?:^|\.)`?([A-Za-z_][A-Za-z0-9_]*)`?$/;

/** A segment's alias, when it names one: the text after AS, or the segment
 *  itself when it is nothing but a column reference. An expression with
 *  neither is not guessed at: guessing wrong here would draw the wrong
 *  reason far more often than dropping it silently. */
function segmentAlias(segment: string): string | undefined {
  const aliased = AS_ALIAS.exec(segment)?.[1];
  if (aliased) {
    return aliased.toLowerCase();
  }
  return /^[A-Za-z_][A-Za-z0-9_.]*$/.test(segment) ? BARE_IDENTIFIER.exec(segment)?.[1]?.toLowerCase() : undefined;
}

function projectedAliases(sql: string): Array<string> {
  const projection = resolvedProjection(sql).trim();
  if (!projection || projection === '*') {
    return [];
  }
  return splitTopLevel(projection)
    .map(segmentAlias)
    .filter((name): name is string => Boolean(name));
}

/**
 * The kind of thing a measure column holds, judged from its name. Coarse on
 * purpose: TTV and margin are both money but a reader who asked for margin
 * would call a TTV number wrong, so money splits into the families a
 * commercial reader tells apart.
 */
type MeasureFamily = 'ratio' | 'count' | 'ttv' | 'margin' | 'cost' | 'money';

const LABEL_NAME =
  /_(plan|plans|type|types|name|names|group|groups|class|classes|band|bands|bucket|buckets|tier|tiers|label|labels|category|categories|status)($|_)/i;
const TTV_NAME = /(^|_)(ttv|sales|revenue|gmv|gsv)($|_)/i;
const MARGIN_NAME = /(^|_)(margin|profit|gm)($|_)/i;
const COST_NAME = /(^|_)(cost|costs|spend|cogs)($|_)/i;
const COUNT_NAME =
  /(^|_)(count|counts|bookings|orders|customers|members|sessions|visits|guests|nights|rooms|units|qty|quantity|n|num|number)($|_)/i;

function measureFamily(column: string): MeasureFamily | null {
  if (!column || LABEL_NAME.test(column)) {
    return null;
  }
  if (RATIO_NAME.test(column) || PRE_AVERAGED_NAME.test(column) || /_(pct|percent)$/i.test(column)) {
    return 'ratio';
  }
  if (TTV_NAME.test(column)) return 'ttv';
  if (MARGIN_NAME.test(column)) return 'margin';
  if (COST_NAME.test(column)) return 'cost';
  if (UNIT_SUFFIX.test(column) || HEADLINE_MEASURE.test(column)) return 'money';
  if (COUNT_NAME.test(column)) return 'count';
  return null;
}

/** What a card's own query measures. A query returning only labels and an
 *  identifier is counting rows, which is what a pie of bookings by type is. */
function measureFamilies(columns: ReadonlyArray<string>): Set<MeasureFamily> {
  const families = new Set(columns.map(measureFamily).filter((family): family is MeasureFamily => family !== null));
  if (!families.size && columns.some((column) => IDENTIFIER_NAME.test(column))) {
    families.add('count');
  }
  return families;
}

/**
 * Writes one query per proposed dataset, keeping only the ones that come back
 * usable.
 *
 * Shared between combining an existing board and planning a new one: both have
 * a data model in hand and need it turned into queries, and a second copy would
 * drift on the corrective retry that makes this worth doing at all.
 */
export async function writeDatasets({
  pool,
  existingIds,
  filters,
  model,
  onStatus,
  check,
}: {
  pool: BuildPool;
  existingIds: Array<string>;
  filters: Array<LensFilter>;
  model: Consolidation;
  onStatus: (message: string) => void;
  /** Supplied when the query is checked against the warehouse rather than by the model itself. */
  check?: Check;
}): Promise<{ datasets: Array<LensDataset>; idByKey: Map<string, string> }> {
  const filterColumns = filterableColumns(filters);

  onStatus(
    model.datasets.length === 1
      ? `Writing the query for \u201c${model.datasets[0].name}\u201d\u2026`
      : `Writing ${model.datasets.length} queries at once\u2026`,
  );

  // Concurrent, because no proposed dataset reads another: the whole data model
  // is settled before the first query is written. Ids are minted afterwards in
  // declared order, so which branch finishes first cannot change the result.
  const written = await pool.map(model.datasets, (planned, lane) =>
    writeOneDataset(
      planned,
      (question, options) =>
        lane.ask(question, {
          ...(options?.effort ? { effort: options.effort } : {}),
          onProgress: (progress) => onStatus(progress ?? ''),
        }),
      filterColumns,
      model.sources,
      check,
    ),
  );

  const taken = new Set(existingIds);
  const datasets: Array<LensDataset> = [];
  const idByKey = new Map<string, string>();

  for (const entry of written) {
    if (!entry) {
      continue;
    }
    const id = uniqueDatasetId(entry.planned.name, taken);
    idByKey.set(entry.planned.key, id);
    datasets.push({
      id,
      name: entry.planned.name,
      sql: entry.sql,
      ...(entry.planned.dimensions?.length ? { dimensions: entry.planned.dimensions } : {}),
      // Re-checked against the query as written rather than as promised: a
      // dataset whose SQL came back without a column a field reads would carry
      // a field that computes nothing.
      ...(entry.planned.fields?.length ? withCheckedFields(entry.planned.fields, entry.columns) : {}),
      ...(entry.dateFilter ? { dateFilter: entry.dateFilter } : {}),
    });
  }

  return { datasets, idByKey };
}

/**
 * One proposed dataset turned into a query, or nothing.
 *
 * A dataset that cannot be written, or that does not return what it promised,
 * is dropped rather than saved: the cards assigned to it keep their own
 * queries, which still work.
 */
async function writeOneDataset(
  planned: Consolidation['datasets'][number],
  ask: Ask,
  filterColumns: Array<string>,
  sources?: ReadonlyArray<VerifiedSource>,
  check?: Check,
): Promise<{
  planned: Consolidation['datasets'][number];
  sql: string;
  columns: Array<string>;
  dateFilter?: LensDataset['dateFilter'];
} | null> {
  const prompt = (correction?: string) =>
    datasetPrompt({
      ...planned,
      filterColumns,
      ...(sources?.length ? { sources } : {}),
      ...(correction ? { correction } : {}),
    });
  const verdicts = new Map<string, SqlVerdict>();
  const verdictOf = async (sql: string): Promise<SqlVerdict | undefined> => {
    if (!check || !sql) {
      return undefined;
    }
    const known = verdicts.get(sql);
    if (known) {
      return known;
    }
    const verdict = await check(sql);
    verdicts.set(sql, verdict);
    return verdict;
  };
  const rejection = async (sql: string): Promise<Array<string>> => {
    const verdict = await verdictOf(sql);
    return verdict && !verdict.ok ? [`The warehouse rejected that query: ${verdict.error ?? 'it did not run'}.`] : [];
  };

  const asked = await ask(prompt(), { effort: 'low' });
  const first = { ...asked, sql: bestDatasetSql(asked.sqlBlocks, planned.columns) ?? '' };
  let generated = first;

  const faults = [...datasetFaults(first.sql, asked.sqlBlocks, planned.columns), ...(await rejection(first.sql))];
  if (faults.length) {
    const retried = await ask(prompt(faults.join(' ')), { effort: 'low' });
    const second = { ...retried, sql: bestDatasetSql(retried.sqlBlocks, planned.columns) ?? '' };
    generated = preferredDataset(first, second, planned.columns);
  }

  if (generated.streamError || !generated.sql || (await rejection(generated.sql)).length) {
    return null;
  }

  const returned = verdicts.get(generated.sql)?.columns;
  const confirmed = returned
    ? planned.columns.filter((column) => returned.includes(column))
    : planned.columns.filter((column) => returnsDeclaredColumns(generated.sql, [column]));
  if (confirmed.length !== planned.columns.length) {
    return null;
  }

  const declared = extractDateDeclaration(generated.sql);
  const dateColumn = declared.column ?? dateColumnFromBinding(generated.sql);
  const dateFilter =
    acceptsDashboardPeriod(generated.sql).ok && dateColumn
      ? {
          column: dateColumn,
          pruned: true,
          ...(declared.available.length > 1 ? { available: declared.available } : {}),
        }
      : undefined;

  return {
    planned,
    sql: generated.sql,
    columns: returned ?? confirmed,
    ...(dateFilter ? { dateFilter } : {}),
  };
}

/** Keeps only the fields the finished query can actually support, checked
 *  against the columns it was confirmed to return rather than parsed out of
 *  its text. */
function withCheckedFields(
  fields: Array<LensDatasetField>,
  columns: ReadonlyArray<string>,
): { fields: Array<LensDatasetField> } | Record<string, never> {
  const prepared = prepareFields(fields, columns);
  const kept = [...prepared.row, ...prepared.aggregate].map((entry) => entry.field);
  return kept.length ? { fields: kept } : {};
}

/** Dataset ids share the card id space, so a collision makes the spec invalid. */
function uniqueDatasetId(name: string, taken: Set<string>): string {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .replace(/^([^a-z])/, 'dataset-$1')
      .slice(0, 58) || 'dataset';
  let id = base;
  let counter = 2;
  while (taken.has(id)) {
    id = `${base}-${counter}`;
    counter += 1;
  }
  taken.add(id);
  return id;
}
